// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import LazyTippy from "@hardwoods/App/Components/Common/LazyTippy";
import { Container } from "@hardwoods/App/Components/Container/Container";
import LazyImage from "@hardwoods/MobiusOverrides/LazyImage/LazyImage";
import { MappedLink } from "@hardwoods/Overrides/Widgets/Header/MainNavigation";
import { constants } from "@hardwoods/XDL/constants";
import ChevronRight from "@hardwoods/XDL/Icons/ChevronRight";
import { ellipsis, isEventSupported } from "@hardwoods/XDL/Utilities";
import color from "@hardwoods/XDL/Utilities/color";
import EventBus from "@hardwoods/XDL/Utilities/EventBus";
import { stopParentScroll, virtualScroll } from "@hardwoods/XDL/Utilities/scroll";
import { styledScrollbar } from "@hardwoods/XDL/Utilities/SharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { hide } from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { TippyProps } from "@tippyjs/react";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";
import { hideAll, Instance } from "tippy.js";

export interface MenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        trigger?: string;
        onMenuShow?: () => void;
        onMenuHide?: () => void;
        menuType?: "MegaMenu" | "CascadingMenu";
        isHeaderLinkMenu?: boolean;
        displayChangeCustomerLink?: boolean;
        tippyProps?: TippyProps;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

const maxMenuHeight = "calc(100vh - 235px);";
// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            width?: number;
            isMegaMenu?: boolean;
        }
>`
    background: ${getColor("text.inverse")};
    width: ${getProp("width", 175)}px;
    max-width: 25vw;
    max-height: ${maxMenuHeight};
    padding: 16px 0;
    list-style: none;
    margin: 0;
    overflow: auto;

    ${styledScrollbar()}

    ${({ isMegaMenu }) =>
        isMegaMenu &&
        css`
            height: 410px;
            &.sub-menu {
                background: transparent;
            }
        `}

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 18px;
    ${ellipsis(2)};
`;

// eslint-disable-next-line no-unexpected-multiline
const MenuItemStyle = styled.div<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            width?: number;
        }
>`
    position: relative;
    padding: 0;
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus {
        outline:none;
    }

    &:hover,
    &[aria-expanded="true"],
    &:focus {
        background: ${getColor("primary.hover")};
        ${MenuItemText}, ${IconWrapper} {
            color: ${getColor("common.white")};
        }
        + div[data-tippy-root] {
            height: calc(100% - 8px);
        }
    
        + div[data-tippy-root] > .sub-menu {
            height:100%;
        }
    }



    ${StyledButton} {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font-weight: bold;
        padding: 10px 16px;
        transition: background ease-in-out 250ms;
        width: 100%;
    }

    &[aria-expanded="true"] > ${StyledButton},
    ${StyledButton}:hover,${StyledButton}:focus {
        background: ${getColor("primary.hover")};

        ${MenuItemText}, ${IconWrapper} {
            color: ${getColor("common.white")};
        }
    }
    a${StyledButton}:hover > ${MenuItemText},a${StyledButton}:focus > ${MenuItemText} {
        text-decoration: none;
    }

    ${injectCss}
`;

const MenuTrigger = styled.span<
    ThemeProps<BaseTheme> &
        InjectableCss & {
            isHeaderLinkMenu?: boolean;
        }
>`
    .collapse,
    .expand,
    .arrow {
        transition: transform ease-in-out 200ms;
    }
    .collapse {
        transform: scale(0);
        visibility: hidden;
        position: absolute;
    }
    .expand {
        transform: scale(1);
        position: static;
        visibility: visible;
    }

    &[aria-expanded="true"] {
        ${({ isHeaderLinkMenu }) =>
            isHeaderLinkMenu
                ? css`
                      > ${StyledButton}, > ${StyledButton} span {
                          color: ${color("text.link")};
                      }
                  `
                : css`
                      background: ${color("primary.dark")};
                      color: ${color("common.white")};
                  `}

        .arrow {
            transform: rotate(180deg);
        }
        .collapse {
            transform: scale(1);
            position: static;
            visibility: visible;
        }
        .expand {
            transform: scale(0);
            visibility: hidden;
            position: absolute;
        }
    }
`;

const styles = {
    megaMenuContainer: {
        css: css`
            padding-top: 6px;
            padding-bottom: 0;
            width: calc(100vw - 10px);
            border-radius: 0 0 6px 6px;
            background: ${color("common.ui10")};
            box-shadow: inset 0 6px 0 0 ${color("primary.dark")};
            &&& {
                padding-left: 0;
            }
            > ${MenuStyle} {
                border-bottom: 1px solid transparent;
                border-bottom-left-radius: 6px;
            }
        `,
    },
    cascadingMenu: {
        css: css`
            padding-bottom: 20px;
            ${MenuStyle} {
                padding-top: 10px;
                border-top: 6px solid ${color("primary.dark")};
                border-bottom: 1px solid transparent;
                border-bottom-left-radius: 4px;
                border-bottom-right-radius: 4px;
                ${MenuStyle} {
                    margin: 0 -2px;
                    height: 100%;
                }

                div[data-tippy-root] {
                    height: 100%;
                }
            }
        `,
    },
    megaMenuImageContainer: {
        css: css`
            position: absolute;
            width: 100%;
            height: auto;
            max-width: calc(100% - 520px); // 2 menus * menu-width(260);
            right: 20px;
            top: 26px;
            bottom: 20px;
            display: flex;
            justify-content: flex-end;
        `,
    },
    menuImage: {
        css: css`
            img {
                max-height: 100%;
            }
        `,
    },
};

const hideOnPopperBlur = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: Instance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    if (
                        event.relatedTarget &&
                        event.relatedTarget instanceof HTMLElement &&
                        !instance.popper.contains(event.relatedTarget)
                    ) {
                        instance.hide();
                    }
                });
            },
        };
    },
};

const ShowMenu = () => {
    return (
        <>
            <Button
                color="transparent"
                css={css`
                    min-height: 0;
                    padding: 0;
                    margin: 0;
                    border: none;
                    &,
                    &&&:focus,
                    &&&:hover {
                        background: transparent;
                        border-radius: 0;
                    }
                `}
                onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                }}
            >
                <IconMemo src={ChevronRight} size={8} color="common.ui12" />
                <VisuallyHidden>{translate("Open Menu")}</VisuallyHidden>
            </Button>
        </>
    );
};

interface MenuState {
    canBeOpen: boolean;
    menuImg: { imgSrc?: string; imgAlt?: string };
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class TippyMenu extends React.Component<MenuProps, MenuState> {
    static defaultProps: Partial<MenuProps> = {
        maxDepth: 2,
        width: 260,
        trigger: "mouseenter focusin",
    };

    state: MenuState = {
        canBeOpen: true,
        menuImg: { imgSrc: "", imgAlt: "" },
    };

    closeMenu = () => {
        this.setState({ canBeOpen: false }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
                hideAll();
            }, 100),
        );
    };

    scrollFn = (e: any) => {
        const mouseWheelEvent = isEventSupported("mousewheel") ? "mousewheel" : "wheel";
        const subMenuElement = document.querySelectorAll(".sub-menu:hover");
        if (subMenuElement?.length > 0) {
            subMenuElement[0]?.addEventListener(mouseWheelEvent, virtualScroll);
            subMenuElement[0]?.addEventListener("DOMMouseScroll", virtualScroll);
            return;
        }
        virtualScroll(e);
    };

    stopOutsideScroll = () => {
        if (typeof document === "undefined") {
            return;
        }

        const mouseWheelEvent = isEventSupported("mousewheel") ? "mousewheel" : "wheel";
        document.body.addEventListener(mouseWheelEvent, stopParentScroll, { passive: false });
        document.body.addEventListener("DOMMouseScroll", stopParentScroll, { passive: false });

        const mainMenuElement = document.querySelector(".main-menu");

        mainMenuElement?.addEventListener(mouseWheelEvent, this.scrollFn);
        mainMenuElement?.addEventListener("DOMMouseScroll", this.scrollFn);
    };

    allowOutsideScroll = () => {
        if (typeof document === "undefined") {
            return;
        }

        const mouseWheelEvent = isEventSupported("mousewheel") ? "mousewheel" : "wheel";
        document.body.removeEventListener(mouseWheelEvent, stopParentScroll);
        document.body.removeEventListener("DOMMouseScroll", stopParentScroll);

        const mainMenuElement = document.querySelector(".main-menu");
        const subMenusElement = document.querySelectorAll(".sub-menu");
        mainMenuElement?.removeEventListener(mouseWheelEvent, virtualScroll);
        mainMenuElement?.removeEventListener("DOMMouseScroll", virtualScroll);

        subMenusElement?.forEach(element => {
            element?.removeEventListener(mouseWheelEvent, virtualScroll);
            element?.removeEventListener("DOMMouseScroll", virtualScroll);
        });
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        menuItems,
                        menuTrigger,
                        isOpen,
                        onMenuShow,
                        onMenuHide,
                        menuType,
                        tippyProps,
                        isHeaderLinkMenu,
                        displayChangeCustomerLink,
                        ...otherProps
                    } = this.props;

                    const { applyProp, spreadProps } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );

                    const isMegaMenu = menuType === "MegaMenu";

                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                        className: string,
                        width: number,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const parentMenu = React.createRef<HTMLUListElement>();
                        return (
                            <>
                                <MenuStyle
                                    width={width}
                                    css={cssOverrides?.menu}
                                    ref={parentMenu}
                                    isMegaMenu={isMegaMenu}
                                    className={className}
                                >
                                    {childMenuItems
                                        ?.filter(item => {
                                            if (item.excludeFromNavigation) {
                                                return false;
                                            }
                                            if (
                                                item.type === "ChangeCustomerPage" &&
                                                !this.props.displayChangeCustomerLink
                                            ) {
                                                return false;
                                            }
                                            return true;
                                        })
                                        .map((item, index) => {
                                            const maxDepth = item.maxDepth || parentDepth;
                                            const hasChildren =
                                                item.children?.some(link => !link.excludeFromNavigation) &&
                                                currentDepth < maxDepth;
                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={width}
                                                    css={cssOverrides?.menuItem}
                                                    onMouseEnter={e => {
                                                        this.setState({
                                                            menuImg: {
                                                                imgSrc: item.imgSrc,
                                                                imgAlt: item.imgAlt || item.title,
                                                            },
                                                        });
                                                    }}
                                                    onMouseLeave={e => {
                                                        this.setState({
                                                            menuImg: {
                                                                imgSrc: "",
                                                                imgAlt: "",
                                                            },
                                                        });
                                                    }}
                                                >
                                                    <Clickable
                                                        href={item.url}
                                                        onClick={() => {
                                                            item.onClick?.();
                                                            this.closeMenu();
                                                        }}
                                                        {...applyProp("menuItemClickableProps")}
                                                    >
                                                        <MenuItemText {...applyProp("menuItemTypographyProps")}>
                                                            {item.title}
                                                        </MenuItemText>
                                                        {hasChildren && <ShowMenu></ShowMenu>}
                                                    </Clickable>
                                                </MenuItemStyle>
                                            );
                                            return (
                                                <li key={item.title}>
                                                    {!hasChildren && menuItem}
                                                    {hasChildren && (
                                                        <LazyTippy
                                                            render={() =>
                                                                renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                    "sub-menu",
                                                                    260,
                                                                )
                                                            }
                                                            trigger="mouseenter click"
                                                            placement="right-start"
                                                            interactive={true}
                                                            animation={false}
                                                            offset={[0, -2]}
                                                            delay={[100, 350]}
                                                            touch={item.url ? "hold" : true}
                                                            plugins={[hideOnPopperBlur]}
                                                            getReferenceClientRect={() => {
                                                                if (
                                                                    !parentMenu.current ||
                                                                    typeof parentMenu.current[
                                                                        "getBoundingClientRect"
                                                                    ] === "undefined"
                                                                ) {
                                                                    return new DOMRect(0, 0, 0, 0);
                                                                }
                                                                return parentMenu.current!.getBoundingClientRect();
                                                            }}
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "preventOverflow",
                                                                        options: { mainAxis: false },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            {menuItem}
                                                        </LazyTippy>
                                                    )}
                                                </li>
                                            );
                                        })}
                                </MenuStyle>
                            </>
                        );
                    };

                    const descriptionId = otherProps.descriptionId || uniqueId();
                    const currentDepth = 1;

                    let mainMenuTippyProps: TippyProps = {};
                    if (typeof document !== "undefined" && isMegaMenu) {
                        const element = document.getElementsByClassName("MainNavigationContainer")[0];
                        mainMenuTippyProps = {
                            popperOptions: {
                                modifiers: [
                                    {
                                        name: "preventOverflow",
                                        options: { boundary: element },
                                    },
                                ],
                            },
                        };
                    }

                    return (
                        <>
                            <LazyTippy
                                render={() => (
                                    <>
                                        {isMegaMenu ? (
                                            <Container {...styles.megaMenuContainer}>
                                                {renderMenuItems(
                                                    otherProps.maxDepth || 0,
                                                    menuItems,
                                                    currentDepth,
                                                    "main-menu",
                                                    otherProps.width ?? 260,
                                                )}

                                                {this.state.menuImg?.imgSrc && (
                                                    <StyledWrapper {...styles.megaMenuImageContainer}>
                                                        <LazyImage
                                                            src={this.state.menuImg.imgSrc}
                                                            altText={this.state.menuImg.imgAlt}
                                                            {...styles.menuImage}
                                                        />
                                                    </StyledWrapper>
                                                )}
                                            </Container>
                                        ) : (
                                            <StyledWrapper {...styles.cascadingMenu}>
                                                {renderMenuItems(
                                                    otherProps.maxDepth || 0,
                                                    menuItems,
                                                    currentDepth,
                                                    "main-menu",
                                                    otherProps.width ?? 260,
                                                )}
                                            </StyledWrapper>
                                        )}
                                    </>
                                )}
                                trigger={this.props.trigger}
                                placement="bottom-start"
                                interactive={true}
                                animation={false}
                                delay={[350, 500]}
                                offset={[0, -1]}
                                plugins={[hideOnPopperBlur]}
                                touch={["hold", 250]}
                                arrow={false}
                                {...mainMenuTippyProps}
                                onShow={() => {
                                    hideAll();
                                    onMenuShow && onMenuShow();
                                    EventBus.dispatch(constants.customEvents.forceShowHeaderPopupOverlay, {
                                        forceShow: true,
                                    });
                                }}
                                onMount={() => {
                                    this.stopOutsideScroll();
                                }}
                                onHide={() => {
                                    EventBus.dispatch(constants.customEvents.forceShowHeaderPopupOverlay, {
                                        forceShow: false,
                                    });
                                    this.allowOutsideScroll();
                                    onMenuHide && onMenuHide();
                                }}
                                {...tippyProps}
                            >
                                <MenuTrigger isHeaderLinkMenu={isHeaderLinkMenu}>
                                    {React.cloneElement(menuTrigger, {
                                        id: descriptionId,
                                        className: "trigger",
                                    })}
                                </MenuTrigger>
                            </LazyTippy>
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(TippyMenu);

export { MenuItemText };
